import { Injectable, Optional } from '@angular/core';
import { IPSObservabilityService } from '@ips/common-angular';
import { ClaimDocumentItemList, ClaimItem } from 'com.trumbull.phx.claim.api-client';
import { DocumentItemList1, DocumentListReference, DocumentService } from 'com.trumbull.phx.document.api-client';
import { OrderItem } from 'com.trumbull.phx.order.api-client';
import { RecordRetrievalRequestItem } from 'com.trumbull.phx.recordretrievalobjective.api-client';
import * as _ from 'lodash';
import { catchError, finalize, forkJoin, map, Observable, of } from 'rxjs';
import { RootObject } from '../../orders/order-details/orders.model';
import { DocumentReference, RecordDetails, TableData } from '../../orders/order-details/record-index-table/record-index-table.component';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class DocumentProcessingService {
  constructor(private documentService: DocumentService, private datePipe: DatePipe, @Optional() private readonly observability: IPSObservabilityService) { }

  getClaimDocuments(claim: ClaimItem) {
    return [
      ...this.getClaimLevelDocuments(claim),
      ...this.getDocumentListsFromFeatures(claim)
    ];
  }

  private getClaimLevelDocuments(claim: ClaimItem) {
    return claim.documentLists || [];
  }

  private getDocumentListsFromFeatures(claim: ClaimItem) {
    return claim.features?.flatMap(feature => feature.documentLists || []) || [];
  }

  getDocumentListObservables(allDocumentLists: ClaimDocumentItemList[]) {
    return allDocumentLists
      .filter(list => typeof list?.documentListId === 'string')
      .map(documentList =>
        this.documentService.getDocumentList('standard', documentList.documentListId!).pipe(
          catchError(error => {
            console.error(`Error fetching document list ${documentList.documentListId}:`, error);
            return of(undefined);
          })
        )
      );
  }

  filterDocumentReferences(
    documentReferences: (DocumentItemList1 | undefined)[],
    documentPattern: RegExp
  ): { processedDocs: (DocumentItemList1 | undefined)[]; stats: { [key: string]: number } } {
    const stats = {
      totalIncoming: documentReferences.length,
      aboveZeroCount: 0,
      zeroCount: 0,
      subrosourcedMatched: 0,
      subrosourceUnmatched: 0,
      paternMatched: 0,
      patternUnmatched: 0
    };

    const processedDocs = documentReferences
      .filter(doc => {
        const hasValidCount = doc && typeof doc.count !== 'undefined' && doc.count > 0;
        if (hasValidCount) {
          stats.aboveZeroCount++;
        } else {
          stats.zeroCount++;
        }
        return hasValidCount;
      })
      .filter(doc => {
        const patternMatches = !doc?.lists?.some((list: DocumentListReference) =>
          !documentPattern.test(list?.name || '') && !list?.name?.includes('Subrosource')
        );
        if (patternMatches) {
          stats.subrosourcedMatched++;
        } else {
          stats.subrosourceUnmatched++;
        }
        return patternMatches;
      })
      .filter(doc => {
        if (!doc?.lists) {
          stats.patternUnmatched++;
          return false;
        }

        const hasMatchingList = doc.lists.some((list: DocumentListReference) => {
          const match = list?.name?.match(documentPattern);

          if (!match) {
            return false;
          }

          // Keep request number as string to support hierarchical formats (e.g., "001", "001-01")
          const requestNumberStr: string = match[1];
          (list as any).requestNumberStr = requestNumberStr;

          return true;
        });

        if (hasMatchingList) {
          stats.paternMatched++;
        } else {
          stats.patternUnmatched++;
        }

        return hasMatchingList;
      });

    return { processedDocs, stats };
}

matchRequestsToDocuments(
  documentReferences: (DocumentItemList1 | undefined)[],
  request: RecordRetrievalRequestItem,
  vocabDocumentTagsData: any
): DocumentReference[] {

  const matchStats = {
    totalDocs: documentReferences.length,
    matchedByRequest: 0,
    matchedByList: 0,
    validReferences: 0
  };

  const requestNumberStr = `${request.requestNumber} - outgoing`.toLowerCase();

  const requestMatchedDocs = documentReferences
    .filter(doc => {
      const nameMatches = doc?.name?.toLowerCase().startsWith(requestNumberStr);
      // Use string comparison to support hierarchical request numbers (e.g., "001-01")
      const listMatches = doc?.lists?.some((list: any) =>
        list.requestNumberStr === request.requestNumber
      );

      if (nameMatches && listMatches) {
        matchStats.matchedByRequest++;
        return true;
      }
      return false;
    })
    .flatMap(doc => {
      // Use string comparison to support hierarchical request numbers (e.g., "001-01")
      const matchedLists = doc?.lists?.filter((list: any) =>
        list.requestNumberStr === request.requestNumber
      ) || [];

      matchStats.matchedByList += matchedLists.length;

      return matchedLists.map((list: any) => {
        const docItems = doc?.documentItems?.filter(item =>
          item.documentListId === list.id
        ) || [];

        matchStats.validReferences += docItems.length;

        return docItems.map(docItem => ({
          documentId: docItem.id,
          fileName: docItem.fileName,
          tags: this.getDocumentTagsDescription(docItem.tags, vocabDocumentTagsData),
          documentListId: list.id
        } as DocumentReference));
      }).flat();
    })
    .filter((doc): doc is DocumentReference => doc !== undefined);

  this.observability?.trackEvent('document:processing:requestMatching', matchStats);

  return requestMatchedDocs;
}

  // here be skeletons
  mapRequestToTableData(
    request: RecordRetrievalRequestItem,
    idx: number,
    order: OrderItem,
    orderDetails: RootObject,
    documentReferences?: (DocumentItemList1 | undefined)[],
    vocabData?:any,
    commentList?: any
  ): TableData {
    let requestMatchedDocs;
    let requestedRecordTypes;
    const vocabRecordTypesData = vocabData?.find((d: any) => d.vocabularyName === 'RecordTypes');
    const vocabDocumentTagsData = vocabData?.find((d: any) => d.vocabularyName === 'DocumentTypeCode');
    if (vocabRecordTypesData) {
      requestedRecordTypes = _.filter(vocabRecordTypesData.vocabularyItems, item => {
        return _.some(request.scope, { recordTypeCode: item.code });
      });
    }
    if (documentReferences) {
      requestMatchedDocs = this.matchRequestsToDocuments(documentReferences, request, vocabDocumentTagsData);
    }

    const retrievalSubject = orderDetails.retrievalObjective?.parties?.find(
      p => p.roles?.some(
        r => r.roleCode === 'record-subject' && 
        r.requestNumbers?.includes(request.requestNumber ?? '')
      )
    );

    const providerSubject = orderDetails.retrievalObjective?.parties?.find(
      p => p.roles?.some(
        r => r.roleCode === 'records-custodian' &&
          r.requestNumbers?.includes(request.requestNumber ??'')
      )
    );

    const doctor = orderDetails.retrievalObjective?.parties?.find(
      p => p.roles?.some(
        r => r.roleCode === 'medical-provider' &&
          r.requestNumbers?.includes(request.requestNumber??'')
      )
    );

    const providerAddress = providerSubject?.party?.locators?.find(locator => locator.typeCode === 'location');
    const providerEmail = providerSubject?.party?.locators?.find(locator => locator.typeCode === 'email');
    const providerPhoneNumber = providerSubject?.party?.locators?.find(locator => locator.typeCode === 'phone');
    const details: RecordDetails = {};
    request.scope?.forEach(scope => {
    // Map imaging details
    if (scope.recordTypeCode === 'diagnostic-images') {

      let diagnosticQualifiers : {
        diagnosticTypeCodes?: string,
        diagnosticFocusArea?: string,
        diagnosticComments?: string
      } = scope.qualifiers ?? {};

      details.imaging = {
        type: diagnosticQualifiers.diagnosticTypeCodes || '--',
        bodyPart: diagnosticQualifiers.diagnosticFocusArea || '--',
        comments: diagnosticQualifiers.diagnosticComments || '--'
      };
    }

      // Map ambulance details
      if (scope.recordTypeCode === 'ambulance') {
        const ambulancePickupAddress = [_.get(scope.qualifiers, 'ambulancePickupLine1') ?? ''
                                      , _.get(scope.qualifiers, 'ambulancePickupCity') ?? ''
                                      , this.getStateDescription(_.get(scope.qualifiers, 'ambulancePickupState'), vocabData) ?? ''
                                      , _.get(scope.qualifiers, 'ambulancePickupPostal') ?? ''];
        const ambulanceDropAddress = [_.get(scope.qualifiers, 'ambulanceDropOffLine1') ?? ''
                                      , _.get(scope.qualifiers, 'ambulanceDropOffCity') ?? ''
                                      , this.getStateDescription(_.get(scope.qualifiers, 'ambulanceDropOffState'), vocabData) ?? ''
                                      , _.get(scope.qualifiers, 'ambulanceDropOffPostal') ?? ''];

        details.ambulance = {
          dateOfService: this.formatDate(_.get(scope.qualifiers, 'ambulanceDateOfService')) || '--',
          timeOfService: _.get(scope.qualifiers, 'ambulanceTimeOfService') || '--',
          pickupAddress: _.join(_.filter(ambulancePickupAddress, Boolean), ", ") || '--',
          dropAddress: _.join(_.filter(ambulanceDropAddress, Boolean), ", ") || '--',
        };
      }

    let subjectSsn = retrievalSubject?.roles?.find(role => role.roleCode === 'record-subject')?.recordSubject?.socialSecurityNumber;

    // Map employment details
    if (scope.recordTypeCode === 'employment') {
      let subjectSsn = retrievalSubject?.roles?.find(role => role.roleCode === 'record-subject')?.recordSubject?.socialSecurityNumber;
      details.employment = {
        ssn: subjectSsn || '--'
      };
    }

    // Map insurance details
    if (scope.recordTypeCode === 'insurance') {
      let insuranceQualifiers : {
        insuranceClaimNumber?: string,
        insurancePolicyNumber?: string
      } = scope.qualifiers ?? {};

      details.insurance = {
        claimNumber: insuranceQualifiers.insuranceClaimNumber || '--',
        policyNumber: insuranceQualifiers.insurancePolicyNumber || '--'
      };
    }

    // Map IRS details
    if (scope.recordTypeCode === 'irs') {
      details.irs = {
        ssn: subjectSsn || '--'
      };
    }

    // Map military details
    if (scope.recordTypeCode === 'military') {
      details.military = {
        branch: _.get(scope.qualifiers,'militaryBranch') || '--',
        serviceNumber: _.get(scope.qualifiers,'militaryServiceNumber') || '--'
      };
    }

    // Map social security details
    if (scope.recordTypeCode === 'social-security') {
      const location = retrievalSubject?.party?.locators?.find(l => l.typeCode === 'location');
      const ssnAddress = [location?.location?.location?.usAddress?.lineOne ?? ''
          , location?.location?.location?.usAddress?.city ?? ''
          , this.getStateDescription(location?.location?.location?.usAddress?.stateCode, vocabData) ?? '' 
          , location?.location?.location?.usAddress?.postalCode ?? ''];
      details.socialSecurity = {
        ssn: subjectSsn || '--',
        address: _.join(_.filter(ssnAddress, Boolean), ", ") || '--',
      };
    }

    // Map union details
    if (scope.recordTypeCode === 'union') {
      details.union = {
        membershipNumber: _.get(scope.qualifiers,'unionMembershipNumber') || '--'
      };
    }

    // Map VA details
    if (scope.recordTypeCode === 'va') {
      let vaQualifiers : {
        vaMedicalRecords?: any,
        vaBenefitRecords?: any
      } = scope.qualifiers ?? {};

      let vaMedicalRecords = '';
      let vaBenefitRecords = '';
      
      const vocabVaTypeData = vocabData?.find((d: any) => d.vocabularyName === 'VA_Options');
      vocabVaTypeData?.vocabularyItems?.forEach((item: any) => {
        if (item.code == vaQualifiers?.vaMedicalRecords[0] || item.code == vaQualifiers?.vaMedicalRecords) {
          vaMedicalRecords = item.description;
        }
        if (item.code == vaQualifiers?.vaBenefitRecords[0] || item.code == vaQualifiers?.vaBenefitRecords) {
          vaBenefitRecords = item.description;
        }
      });
      let va = (vaMedicalRecords ? vaMedicalRecords : '')
      + (vaMedicalRecords && vaBenefitRecords ? ', ' + vaBenefitRecords : vaBenefitRecords ? vaBenefitRecords : '');
      
     
      details.va = {
        details: va || '--'
      };
    }

    // Map other details
    if (scope.recordTypeCode === 'other') {
      details.other = {
        recordDescription: _.get(scope.qualifiers,'otherDescriptionOfRecords') || '--'
      };
    }
  })

  let scopeItem = request.scope?.at(0);
    const recordTypes = requestedRecordTypes ? _.map(requestedRecordTypes, 'description') : _.map(request?.scope, 'recordTypeCode');
    
    return {
      id: Number.parseInt(request.requestNumber??'0', 10),
      // Keep request number as string to support hierarchical formats (e.g., "001-01")
      formattedId: `${order.orderNumber}-${request.requestNumber}` || `${order.id}-${request.requestNumber}`,
      recordSubject: retrievalSubject?.party?.displayName || '--',
      providerName: providerSubject?.party?.displayName || '--',
      doctor: doctor?.party?.displayName || '--',
      address: ([providerAddress?.location?.location?.usAddress?.lineOne, providerAddress?.location?.location?.usAddress?.lineTwo, providerAddress?.location?.location?.usAddress?.city, this.getStateDescription(providerAddress?.location?.location?.usAddress?.stateCode, vocabData), providerAddress?.location?.location?.usAddress?.postalCode].filter(Boolean).join(', ') || '--'),
      email: providerEmail?.email?.address || '--',
      phone: providerPhoneNumber?.phone?.number,
      extension: providerPhoneNumber?.phone?.extension,
      // recordRequestType: scopeItem?.recordTypeCode || '--',
      recordRequestType: _.join(recordTypes, ', ') || '--',
      // dateOfServices: this.formatDateRange(scopeItem?.beginDate, scopeItem?.endDate),
      dateOfServices: (scopeItem?.beginDate || scopeItem?.endDate) ? this.formatDateRange(scopeItem?.beginDate, scopeItem?.endDate) : 'Any or All',
      status: this.getOrderStatusDescription(request.statusCode, vocabData) ?? '--',
      statusMessage: commentList?.[`RR-${request.requestNumber}`]?.statusMessage ?? null,
      statusUpdateDate: commentList?.[`RR-${request.requestNumber}`]?.statusUpdatedDate ?? null,
      clientActionRequired: request.clientActionRequired ?? false,
      additionalInfo: {
        isCollapsed: true,
        details
      },
      documents: requestMatchedDocs ?? []
    };
  }

  formatDate(date?: string): string {
    // return this.datePipe.transform(date, 'MM/dd/yyyy', 'UTC') || '';

    if (!date) return '';

    let parsedDate: Date;

    // Check if date includes time or timezone info
    const hasTimeOrZone = /T|\d{2}:\d{2}/.test(date);

    if (!hasTimeOrZone) {
      parsedDate = new Date(date + 'T00:00:00Z');
    } else {
      parsedDate = new Date(date);
    }

    return this.datePipe.transform(parsedDate, 'MM/dd/yyyy') || '';
  }

  private formatDateRange(beginDate?: string, endDate?: string): string {
    if (!beginDate && !endDate) return '--';
  
    // const formatDate = (date?: string) => {
    //   if (!date) return '';
    //   // const parsed = new Date(date);
      
    //   // // Format as MM/dd/yyyy
    //   // const month = (parsed.getMonth() + 1).toString().padStart(2, '0');
    //   // const day = parsed.getDate().toString().padStart(2, '0');
    //   // const year = parsed.getFullYear();
      
    //   // return `${month}/${day}/${year}`;
    //   return this.datePipe.transform(date, 'MM/dd/yyyy', 'UTC') || '';
    // };
  
    const start = this.formatDate(beginDate);
    const end = this.formatDate(endDate);
  
    if (start && end) {
      return `${start} - ${end}`;
    }
    
    return start || end || '--';
  }

  getStateDescription(state: string = '', vocabData: any) {
    const vocabCityData = vocabData?.find((d: any) => d.vocabularyName === 'PhysicalLocationUsAddressStateCode');
    const selectedCity = vocabCityData?.vocabularyItems?.find((item: any) => item?.code === state);
    return selectedCity?.description;
  }

  getOrderStatusDescription(code: string = '', vocabData: any) {
    const selectedStatus =
      vocabData?.find((d: any) => d.vocabularyName === 'OrderStatusCode')
        ?.vocabularyItems?.find((item: any) => item?.code === code)
      ||
      vocabData?.find((d: any) => d.vocabularyName === 'RecordRetrievalRequestStatusCode')
        ?.vocabularyItems?.find((item: any) => item?.code === code);
     return selectedStatus?.description ?? code;
  }

  mapRequests(
    order: OrderItem, 
    orderDetails: RootObject,
    documentReferences?: (DocumentItemList1 | undefined)[],
    vocabData?: any,
    commentList?: any
  ): TableData[] {
    return orderDetails.retrievalObjective?.requests?.map((request, idx) =>
      this.mapRequestToTableData(request, idx, order, orderDetails, documentReferences, vocabData, commentList)
    ) || [];
  }

  fetchDocuments2(
    allDocumentListIds: string[],
    setLoading: (loading: boolean) => void
  ): Observable<DocumentItemList1[]> {
    const documentObservables = allDocumentListIds.map(documentListId =>
      this.documentService.getDocumentList('standard', documentListId).pipe(
        catchError(error => {
          console.error(`Error fetching document list ${documentListId}:`, error);
          return of(undefined);
        })
      )
    )

    return forkJoin(documentObservables).pipe(
      map(docs => docs.filter((doc): doc is DocumentItemList1 => doc !== undefined)),
      finalize(() => setLoading?.(false))
    );
  }

  fetchDocuments(
    claim: ClaimItem,
    setLoading: (loading: boolean) => void
  ): Observable<DocumentItemList1[]> {
    const allDocumentLists = this.getClaimDocuments(claim);
    const documentObservables = this.getDocumentListObservables(allDocumentLists);

    return forkJoin(documentObservables).pipe(
      map(docs => docs.filter((doc): doc is DocumentItemList1 => doc !== undefined)),
      finalize(() => setLoading?.(false))
    );
  }

  processDocumentsAndMapRequests(
    claim: ClaimItem,
    order: OrderItem,
    orderDetails: RootObject,
    setLoading: (loading: boolean) => void
  ): Observable<TableData[]> {
    const allDocumentLists = this.getClaimDocuments(claim);
    const documentObservables = this.getDocumentListObservables(allDocumentLists);
    const documentPattern = /^[^-]+ - (\d+(?:-\d+)*) - [^-]+ - [^-]+$/;

    return forkJoin(documentObservables).pipe(
      map(documentReferences => {
        const processedDocs = this.filterDocumentReferences(documentReferences, documentPattern);

        this.observability?.trackEvent('document:processing:filter:results', processedDocs.stats);

        return orderDetails.retrievalObjective?.requests?.map((request, idx) =>
          this.mapRequestToTableData(request, idx, order, orderDetails, documentReferences)
        ) || [];
      }),
      finalize(() => setLoading?.(false))
    );
  }

  getDocumentTagsDescription(tags: any, vocabDocumentTagsData: any) {
    let documentTags;
    if (vocabDocumentTagsData) {
      documentTags = vocabDocumentTagsData.vocabularyItems.filter((item: any) =>
        tags.some((tag: any) => tag.tagName === item.code)
      );
    }
    return (documentTags?.map((item: any) => item.description))?.join(', ')
  }
}
